import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import html2pdf from "html2pdf.js";
import { QUIZ_SUBMIT_PDF } from "../../../constants/email";

const APP_LOGO =
  "https://cdn.simulanis.io/sso/uno/production/teams/c416d13b-fb0a-431e-899b-e558a2747f52/avatar/cd39c52e-43c8-4d21-9fb3-26f244e336f8.png";

const formatWhen = (value) => (value ? new Date(value).toLocaleString() : "—");

const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const mins = Math.round(Number(seconds) / 60);
  return `${mins} min`;
};

const answerLabel = (item) => {
  if (item?.answer) return item.answer;
  const match = item?.options?.find((option) => option.uuid === item?.responseId);
  return match?.option || "N/A";
};

const taggedAnswers = (row) => [
  ...(Array.isArray(row.entryData) ? row.entryData.map((item) => ({ ...item, phaseLabel: "Entry" })) : []),
  ...(Array.isArray(row.exitData) ? row.exitData.map((item) => ({ ...item, phaseLabel: "Exit" })) : []),
];

const isNegativeSession = (row) => taggedAnswers(row).some((item) => answerLabel(item).toLowerCase() === "no");

const buildPdfBlob = async (row, freezerName) => {
  const answers = taggedAnswers(row);
  const correctQuestions = answers.filter((item) => answerLabel(item).toLowerCase() === "yes").length;
  const wrongQuestions = answers.filter((item) => answerLabel(item).toLowerCase() === "no").length;
  const totalAttachments = answers.reduce((sum, item) => sum + (item.attachments?.length || 0), 0);
  const questionsHtml = answers
    .map((item, index) => {
      const label = answerLabel(item);
      const attachmentsHtml =
        item.attachments?.length > 0
          ? item.attachments
              .map((attachment) => `<div class="attachment-item"><img src="${attachment}" /></div>`)
              .join("")
          : "<p>No attachments</p>";
      return `
        <div class="question-item">
          <div class="question-header">
            <h4 class="question-title">${index + 1}. [${item.phaseLabel}] ${item.question || item.title || ""}</h4>
            <div class="response-status ${label.toLowerCase() === "yes" ? "yes" : label.toLowerCase() === "no" ? "no" : "na"}">${label}</div>
          </div>
          <div class="question-comment">${item.comment || "No Comments"}</div>
          ${label.toLowerCase() === "no" ? `<div class="attachments-section">${attachmentsHtml}</div>` : ""}
        </div>`;
    })
    .join("");

  let html = QUIZ_SUBMIT_PDF;
  html = html.replaceAll("{{SITE_ROW_HERE}}", "");
  html = html.replaceAll("{{APP_LOGO}}", APP_LOGO);
  html = html.replaceAll("{{INSPECTION_NAME}}", "Freezer-Frost");
  html = html.replaceAll("{{SITE_NAME}}", freezerName || "N/A");
  html = html.replaceAll("{{SITE_NAME_LABEL}}", "Freezer");
  html = html.replaceAll("{{DATE}}", moment(row.entryTime || row.createdAt).format("DD MMM YYYY"));
  html = html.replaceAll("{{CORRECT_QUS}}", String(correctQuestions));
  html = html.replaceAll("{{TOTAL_QUS}}", String(answers.length));
  html = html.replaceAll("{{HEALTH}}", String(answers.length ? Math.round((correctQuestions / answers.length) * 100) : 0));
  html = html.replaceAll("{{WRONG_QUS}}", String(wrongQuestions));
  html = html.replaceAll("{{TOTAL_ATTACHMENT}}", String(totalAttachments));
  html = html.replaceAll("{{MACHINE_NAME_LABEL}}", "Freezer");
  html = html.replaceAll("{{MACHINE_NAME}}", freezerName || "N/A");
  html = html.replaceAll("{{MACHINE_ID_LABEL}}", "Session");
  html = html.replaceAll("{{MACHINE_ID}}", row.uuid || "N/A");
  html = html.replaceAll("{{DATE_TIME}}", moment(row.entryTime || row.createdAt).format("MMMM Do YYYY, h:mm:ss a"));
  html = html.replaceAll("{{USER_NAME}}", row.userName || row.userUuid || "N/A");
  html = html.replaceAll("{{RESPONSE_LENGTH}}", String(answers.length));
  html = html.replaceAll("{{REQPONSE_QUESTIONS}}", questionsHtml);

  const element = document.createElement("div");
  element.innerHTML = html;
  return html2pdf()
    .from(element)
    .set({
      margin: 0.3,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    })
    .outputPdf("blob");
};

const FreezerSessionList = ({ setResponseLenght, setTabActions }) => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const downloadNegativePdfs = async () => {
    const negative = rows.filter(isNegativeSession);
    if (!negative.length) {
      window.alert("No negative sessions yet.");
      return;
    }
    setDownloadingZip(true);
    try {
      const JSZip = (await import("jszip")).default;
      const FileSaver = await import("file-saver");
      const saveAs = FileSaver.default.saveAs || FileSaver.saveAs;
      const zip = new JSZip();
      for (const row of negative) {
        const blob = await buildPdfBlob(row, "Freezer-Frost");
        zip.file(`negative-${row.uuid}.pdf`, blob);
      }
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "freezer-negative-responses.zip");
    } catch (error) {
      console.log(error);
    } finally {
      setDownloadingZip(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${configData.config.API_URL}/api/freezer-frost/sessions/${uuid}`, {
        headers: { Authorization: `Bearer ${userData.token}` },
      });
      const list = data.data || [];
      setRows(list);
      setResponseLenght?.(list.length);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uuid && userData?.token) load().catch((error) => console.log(error));
  }, [uuid, userData?.token]);

  useEffect(() => {
    if (!setTabActions) return undefined;
    if (!rows.length) {
      setTabActions(null);
      return undefined;
    }
    setTabActions(
      <div className="entity-info-v2__action-group">
        <a
          className="entity-info-v2__btn-outline"
          href={`${configData.config.API_URL}/api/freezer-frost/sessions/${uuid}/export`}
          onClick={(event) => {
            event.preventDefault();
            axios
              .get(`${configData.config.API_URL}/api/freezer-frost/sessions/${uuid}/export`, {
                headers: { Authorization: `Bearer ${userData.token}` },
                responseType: "blob",
              })
              .then((response) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(response.data);
                link.download = "freezer-sessions.csv";
                link.click();
              })
              .catch((error) => console.log(error));
          }}
        >
          Download Excel
        </a>
        <button type="button" className="entity-info-v2__btn-outline" onClick={downloadNegativePdfs} disabled={downloadingZip}>
          {downloadingZip ? "Preparing PDFs..." : "Download negative PDFs"}
        </button>
      </div>,
    );
    return () => setTabActions(null);
  }, [setTabActions, uuid, configData?.config?.API_URL, userData?.token, rows, downloadingZip]);

  if (loading) return null;

  if (!rows.length) {
    return (
      <div className="ff-empty">
        <strong>No sessions yet</strong>
        <p>
          Sessions show up here after someone scans the Entry QR and later the Exit QR. Each row is one visit, with the
          time spent inside.
        </p>
      </div>
    );
  }

  return (
    <div className="entity-info-v2__table-area">
      <table className="entity-info-v2__table">
        <thead>
          <tr>
            <th>User</th>
            <th>AA Alias</th>
            <th>Entry Time</th>
            <th>Exit Time</th>
            <th>Duration</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
              <tr key={row.uuid}>
                <td>{row.userName || row.userUuid || "—"}</td>
                <td>{row.aaAlias || "—"}</td>
                <td>{formatWhen(row.entryTime)}</td>
                <td>{row.status === "completed" ? formatWhen(row.exitTime) : "—"}</td>
                <td>{formatDuration(row.durationSeconds)}</td>
                <td>
                  {row.nonCompliant || row.status === "closed_non_compliance"
                    ? "Non-compliance (closed)"
                    : row.cumulativeLimitReached && row.status === "completed"
                      ? "Completed — Cumulative time exceeded"
                      : row.cumulativeLimitReached
                        ? "Cumulative limit reached"
                        : row.exitQrMissed
                          ? "Exceeded exposure limit"
                          : row.exposureLimitExceeded && row.status === "completed"
                            ? "Exceeded exposure limit"
                            : row.status === "entry_open"
                              ? "Active inside"
                              : row.status === "completed"
                                ? "Completed"
                                : row.status}
                </td>
                <td>
                  <button type="button" className="plain-btn" onClick={() => navigate(`/ff/session/${row.uuid}`)}>
                    View Details
                  </button>
                </td>
              </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FreezerSessionList;
